import { useEffect, useState } from 'react';

/**
 * The section of the application the user has open, for the feedback button of the top bar:
 * the feedback is logged to it and its averages are counted by it. A page or a part of it
 * (the calculation of a position, the PDF preview) tells its section while it is open; the
 * one opened last is on top. The sections are in Finnish, so the feedback list reads the
 * same in every language.
 */
export interface IFeedbackSection {
  // Exactly where, e.g. the position and the address of the page
  location: string;
  // E.g. "Tarjous yhteenveto", "Positiolaskenta", "Esikatselu"
  section: string;
}

let sections: { id: number; value: IFeedbackSection }[] = [];
let nextId = 0;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

/**
 * Tells the section while the component is shown.
 */
export const useFeedbackSection = (section: string, location = ''): void => {
  useEffect(() => {
    nextId += 1;
    const id = nextId;
    sections = [...sections, { id, value: { location, section } }];
    notify();
    return () => {
      sections = sections.filter((entry) => entry.id !== id);
      notify();
    };
  }, [section, location]);
};

/**
 * The section opened last, null when no page has told one.
 */
export const useCurrentFeedbackSection = (): IFeedbackSection | null => {
  const [current, setCurrent] = useState<IFeedbackSection | null>(
    sections.length > 0 ? sections[sections.length - 1].value : null
  );

  useEffect(() => {
    const listener = () =>
      setCurrent(
        sections.length > 0 ? sections[sections.length - 1].value : null
      );
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return current;
};

// The sections of the pages that do not tell one, by their addresses
const SECTIONS_BY_PATH: [RegExp, string][] = [
  [/^\/$/, 'Etusivu'],
  [/^\/offers\/?$/, 'Tarjouslista'],
  [/^\/offers\/defaults/, 'Tarjousoletukset'],
  [/^\/offers\/products\//, 'Positio'],
  [/^\/offers\//, 'Tarjous yhteenveto'],
  [/^\/reports\/offers/, 'Tarjousdashboard'],
  [/^\/reports\/timeline/, 'Tarjousaikajana'],
  [/^\/reports\/feedback/, 'Palautteet'],
  [/^\/reports\/log/, 'Loki'],
  [/^\/projects/, 'Projektit'],
  [/^\/contacts/, 'Yhteystiedot'],
  [/^\/products/, 'Tuotteet'],
  [/^\/users/, 'Käyttäjähallinta'],
];

export const sectionOfPath = (path: string): string =>
  SECTIONS_BY_PATH.find(([pattern]) => pattern.test(path))?.[1] ?? 'Muu';
